import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  CreditCard,
  ArrowDownToLine,
  ChevronLeft,
  ChevronRight,
  BarChart2,
  ClipboardList,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// CONFIGURAÇÃO CENTRAL DOS PAINÉIS
// Para adicionar novos painéis no futuro, basta adicionar aqui.
// ─────────────────────────────────────────────────────────────
export const PAINEIS_NAV = [

  {
    id: "formulario",
    label: "Formulário (GP)",
    path: "/painel-formulario",
    icon: LayoutDashboard,
    roles: ["admin", "financeiro", "diretor", "onlyFormulario"],
    description: "Formulário de emissão de notas",
  },
  {
    id: "minhasSolicitacoes",
    label: "Minhas Solicitações",
    path: "/minhas-solicitacoes",
    icon: ClipboardList,
    roles: ["admin", "financeiro", "diretor", "GP", "OPERACAO", "GESTOR_ESTRATEGICO", "ASSISTENTEOPERACAO"],
    description: "Solicitações de NFSe enviadas",
  },
    {
    id: "faturamento",
    label: "Faturamento por Projeto",
    path: "/painel-faturamento",
    icon: BarChart2,
    roles: ["admin", "financeiro", "diretor", "GP", "OPERACAO", "GESTOR_ESTRATEGICO", "ASSISTENTEOPERACAO"],
    description: "Faturamento bruto e líquido por projeto",
  },
  {
    id: "competencia",
    label: "Competência",
    path: "/painel-competencia",
    icon: ArrowDownToLine,
    roles: ["admin", "financeiro", "diretor"],
    description: "Gestão de Competência",
  },
  {
    id: "inadimplencia",
    label: "Inadimplência",
    path: "/painel-inadimplencia",
    icon: CreditCard,
    roles: ["admin", "financeiro", "diretor"],
    description: "Gestão de Inadimplência",
  },
];

// ─────────────────────────────────────────────────────────────
// THEME TOKENS — compatível com DARK/LIGHT do painel executivo
// ─────────────────────────────────────────────────────────────
const SIDEBAR_THEME = {
  dark: {
    bg: "#0F1117",
    bgHover: "#1A1D27",
    bgActive: "#1E2235",
    border: "rgba(255,255,255,0.07)",
    text: "#E2E8F0",
    muted: "#64748B",
    activeText: "#FFFFFF",
    activeBorder: "#6366F1",
    brand: "#6366F1",
    brandLight: "rgba(99,102,241,0.15)",
    shadow: "2px 0 24px rgba(0,0,0,0.4)",
    tooltipBg: "#1E2235",
    headerBg: "rgba(99,102,241,0.12)",
    scrollbar: "#1E2235",
  },
  light: {
    bg: "#FFFFFF",
    bgHover: "#F1F5F9",
    bgActive: "#EEF2FF",
    border: "rgba(0,0,0,0.08)",
    text: "#1E293B",
    muted: "#94A3B8",
    activeText: "#4338CA",
    activeBorder: "#4338CA",
    brand: "#4338CA",
    brandLight: "rgba(67,56,202,0.08)",
    shadow: "2px 0 16px rgba(0,0,0,0.08)",
    tooltipBg: "#1E293B",
    headerBg: "rgba(67,56,202,0.06)",
    scrollbar: "#E2E8F0",
  },
};

// ─────────────────────────────────────────────────────────────
// SIDEBAR COMPONENT
// Props:
//   isLight   (bool)   — segue o tema do painel pai
//   userRole  (string) — filtra painéis por role (opcional)
//   onNavigate (fn)    — callback após navegação (opcional)
// ─────────────────────────────────────────────────────────────
export default function SideBarNavFaturamento({
  isLight = false,
  userRole,
  onNavigate,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0 });
  const itemRefs = useRef({});

  const navigate = useNavigate();
  const location = useLocation();
  const T = isLight ? SIDEBAR_THEME.light : SIDEBAR_THEME.dark;

  // Filtra painéis pelo role do usuário (se informado)
  const paineisVisiveis = userRole
    ? PAINEIS_NAV.filter((p) => p.roles.includes(userRole))
    : PAINEIS_NAV;

  const isActive = (path) => location.pathname === path;

  const handleNav = (path) => {
    navigate(path);
    if (onNavigate) onNavigate(path);
  };

  const WIDTH_EXPANDED = 220;
  const WIDTH_COLLAPSED = 62;
  const width = collapsed ? WIDTH_COLLAPSED : WIDTH_EXPANDED;

  return (
    <>
      <style>{`
        .snav-item { transition: background 0.15s, color 0.15s; }
        .snav-item:hover { background: var(--snav-hover) !important; }
        .snav-item:active { transform: scale(0.98); }
        .snav-label { 
          transition: opacity 0.18s, width 0.18s, transform 0.18s;
          white-space: nowrap;
          overflow: hidden;
        }
        .snav-tooltip {
          animation: tooltipIn 0.12s ease forwards;
        }
        @keyframes tooltipIn {
          from { opacity:0; transform:translateX(-4px); }
          to   { opacity:1; transform:translateX(0); }
        }
        .snav-toggle { transition: background 0.15s, transform 0.2s; }
        .snav-toggle:hover { background: var(--snav-hover) !important; }
        .snav-section-label {
          transition: opacity 0.18s;
        }
      `}</style>

      <aside
        style={{
          "--snav-hover": T.bgHover,
          width,
          minWidth: width,
          maxWidth: width,
          height: "100vh",
          background: T.bg,
          borderRight: `1px solid ${T.border}`,
          boxShadow: T.shadow,
          display: "flex",
          flexDirection: "column",
          transition:
            "width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1)",
          position: "sticky",
          top: 0,
          zIndex: 40,
          overflow: "hidden",
          fontFamily: "'DM Sans','Segoe UI',sans-serif",
        }}
      >
        {/* ── Logo / Header ── */}
        <div
          style={{
            padding: collapsed ? "18px 0" : "18px 16px",
            borderBottom: `1px solid ${T.border}`,
            background: T.headerBg,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            minHeight: 62,
            gap: 10,
          }}
        >
          {!collapsed && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: T.brand,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BarChart2 size={15} color="#fff" />
              </div>
              <div style={{ overflow: "hidden" }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.text,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  YOUX GROUP
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontWeight: 500 }}>
                  Faturamentos
                </div>
              </div>
            </div>
          )}

          {collapsed && (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: T.brand,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarChart2 size={15} color="#fff" />
            </div>
          )}
        </div>

        {/* ── Seção: Painéis ── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "10px 8px",
            overflowX: "hidden",
          }}
        >
          {!collapsed && (
            <div
              className="snav-section-label"
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: T.muted,
                textTransform: "uppercase",
                padding: "6px 8px 8px",
                opacity: collapsed ? 0 : 1,
              }}
            >
              Painéis
            </div>
          )}

          {paineisVisiveis.map((painel) => {
            const Icon = painel.icon;
            const active = isActive(painel.path);
            const hovered = hoveredId === painel.id;

            return (
              <div
                key={painel.id}
                ref={(el) => (itemRefs.current[painel.id] = el)}
                className="snav-item"
                onMouseEnter={(e) => {
                  setHoveredId(painel.id);
                  if (collapsed && itemRefs.current[painel.id]) {
                    const rect =
                      itemRefs.current[painel.id].getBoundingClientRect();
                    setTooltipPos({ top: rect.top + rect.height / 2 });
                  }
                }}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => handleNav(painel.path)}
                title={collapsed ? painel.label : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: collapsed ? "10px 0" : "10px 10px",
                  borderRadius: 8,
                  cursor: "pointer",
                  marginBottom: 2,
                  justifyContent: collapsed ? "center" : "flex-start",
                  background: active ? T.bgActive : "transparent",
                  borderLeft: active
                    ? `3px solid ${T.activeBorder}`
                    : "3px solid transparent",
                  position: "relative",
                  userSelect: "none",
                }}
              >
                {/* Ícone */}
                <Icon
                  size={18}
                  color={active ? T.brand : hovered ? T.text : T.muted}
                  strokeWidth={active ? 2.2 : 1.8}
                  style={{ flexShrink: 0, transition: "color 0.15s" }}
                />

                {/* Label */}
                {!collapsed && (
                  <div
                    className="snav-label"
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      color: active ? T.activeText : T.text,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {painel.label}
                  </div>
                )}

                {/* Indicador ativo (ponto) no modo colapsado */}
                {collapsed && active && (
                  <div
                    style={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: T.brand,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* ── Botão Colapsar ── */}
        <div
          style={{
            borderTop: `1px solid ${T.border}`,
            padding: "10px 8px",
          }}
        >
          <button
            className="snav-toggle"
            onClick={() => setCollapsed((v) => !v)}
            style={{
              "--snav-hover": T.bgHover,
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 8,
              width: "100%",
              padding: collapsed ? "9px 0" : "9px 10px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: T.muted,
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "inherit",
            }}
          >
            {collapsed ? (
              <ChevronRight size={16} color={T.muted} />
            ) : (
              <>
                <ChevronLeft size={16} color={T.muted} />
                <span>Recolher</span>
              </>
            )}
          </button>
        </div>

        {/* ── Tooltip flutuante (modo colapsado) ── */}
        {collapsed &&
          hoveredId &&
          (() => {
            const painel = PAINEIS_NAV.find((p) => p.id === hoveredId);
            if (!painel) return null;
            return (
              <div
                className="snav-tooltip"
                style={{
                  position: "fixed",
                  left: WIDTH_COLLAPSED + 12,
                  top: tooltipPos.top,
                  transform: "translateY(-50%)",
                  background: T.tooltipBg,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: 7,
                  pointerEvents: "none",
                  zIndex: 9999,
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  fontFamily: "'DM Sans','Segoe UI',sans-serif",
                }}
              >
                {painel.label}
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: "rgba(255,255,255,0.6)",
                    marginTop: 1,
                  }}
                >
                  {painel.description}
                </div>
              </div>
            );
          })()}
      </aside>
    </>
  );
}
